import React from 'react';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, Award, ArrowRight } from 'lucide-react';

const PolicySection = () => {
  const policies = [
    {
      icon: Lock,
      title: "Chính sách bảo mật",
      description: "Chúng tôi cam kết bảo vệ thông tin cá nhân của bạn. Dữ liệu được mã hóa và chỉ sử dụng cho mục đích cung cấp dịch vụ.",
      color: "blue",
      delay: 0
    },
    {
      icon: CheckCircle,
      title: "Điều khoản sử dụng",
      description: "Người dùng phải tuân thủ các điều khoản sử dụng khi truy cập và sử dụng hệ thống DVT Hulo.",
      color: "green",
      delay: 0.2
    },
    {
      icon: Award,
      title: "Quy trình duyệt",
      description: "Sau khi đăng ký, tài khoản sẽ được admin duyệt trong vòng 24h. Bạn sẽ nhận được thông báo khi được phê duyệt.",
      color: "yellow",
      delay: 0.4
    }
  ];

  const colorClasses = {
    blue: "text-blue-600 border-blue-600 hover:bg-blue-600",
    green: "text-green-600 border-green-600 hover:bg-green-600",
    yellow: "text-yellow-600 border-yellow-600 hover:bg-yellow-600"
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-base text-blue-400 font-semibold tracking-wide uppercase mb-4">
            Chính sách
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Chính sách của chúng tôi
          </h3>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Cam kết mang đến trải nghiệm sử dụng an toàn và minh bạch cho tất cả người dùng.
          </p>
        </motion.div>

        {/* Policy Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {policies.map((policy, index) => (
            <motion.div
              key={index}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: policy.delay }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="group"
            >
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-8 border border-white/20 hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10 opacity-50"></div>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`flex items-center justify-center w-16 h-16 rounded-2xl border-2 ${colorClasses[policy.color]} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <policy.icon className="w-8 h-8" />
                  </div>

                  {/* Content */}
                  <h4 className="text-xl font-semibold text-white mb-4">{policy.title}</h4>
                  <p className="text-gray-300 leading-relaxed mb-6">{policy.description}</p>

                  {/* CTA */}
                  <div className={`flex items-center text-sm font-medium ${colorClasses[policy.color]} group-hover:text-white transition-colors duration-300`}>
                    <span>Xem chi tiết</span>
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>

                {/* Hover Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${policy.color === 'blue' ? 'from-blue-500 to-blue-600' : policy.color === 'green' ? 'from-green-500 to-green-600' : 'from-yellow-500 to-yellow-600'} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-lg p-8 border border-white/20">
            <h4 className="text-2xl font-bold text-white mb-4">
              Bạn có câu hỏi?
            </h4>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Đội ngũ hỗ trợ của chúng tôi luôn sẵn sàng giải đáp mọi thắc mắc và hỗ trợ bạn trong quá trình sử dụng hệ thống.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-full hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Liên hệ hỗ trợ
              <ArrowRight className="ml-2 h-5 w-5" />
            </motion.button>
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

export default PolicySection; 